import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid2 as Grid,
  Checkbox,
  FormControlLabel,
  Button,
  CircularProgress,
  Paper,
  alpha,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
} from '@mui/material';
import {
  SaveOutlined,
  CloseOutlined,
  SecurityOutlined,
  CheckCircleOutline,
} from '@mui/icons-material';
import { updateRole } from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';
import ConfirmationModal from '../ConfimationModal/ConfirmationModal';

const EditRoleForm = ({
  permissions,
  closeEditModal,
  name,
  desc,
  privileges,
  id,
  userCategory,
}) => {
  // State management preserved exactly as original
  const [formData, setFormData] = useState({
    roleName: name,
    roleDescription: desc,
    userCategory: userCategory,
  });
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [failedMessage, setFailedMessage] = useState('');
  const [permissionsList, setPermissionsList] = useState([]);

  useEffect(() => {
    // Compile initial privileges into the ID list
    if (privileges && privileges.length > 0) {
      const initialIds = privileges.map((p) => p.privilegeId);
      setPermissionsList(initialIds);
    }
  }, [privileges]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const closeModal = () => {
    setVisible(false);
    window.location.reload();
  };

  // Logic for toggling permissions preserved
  const handleCheckboxChange = (e) => {
    const privilegeId = e.target.id;
    if (e.target.checked) {
      setPermissionsList((prev) => [...prev, privilegeId]);
    } else {
      setPermissionsList((prev) => prev.filter((id) => id !== privilegeId));
    }
  };

  const handleEditRole = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFailedMessage('');

    if (permissionsList.length === 0) {
      openNotification({ type: 'warning', message: 'Please select at least one permission' });
      setLoading(false);
      return;
    }

    const payload = {
      roleId: id,
      roleName: formData.roleName.toLowerCase(),
      roleDescription: formData.roleDescription.toLowerCase(),
      privilegeId: permissionsList,
    };

    try {
      const res = await updateRole(payload);
      if (res === null) {
        setFailedMessage('No changes were made to this role');
      } else if (res.result.status.code === '00') {
        setConfirmMessage('Role updated successfully!');
        setVisible(true);
      }
    } catch (error) {
      const errmsg = error?.data?.result?.desc || error.message;
      setFailedMessage(errmsg);
      openNotification({
        type: 'error',
        title: 'Error updating role',
        message: `${errmsg}`,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleEditRole}>
      <ConfirmationModal open={visible} close={closeModal} message={confirmMessage} />

      {failedMessage && (
        <Typography
          color="error"
          variant="caption"
          sx={{ mb: 2, display: 'block', fontWeight: 600 }}
        >
          {failedMessage}
        </Typography>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Role Name"
            name="roleName"
            value={formData.roleName}
            onChange={handleInputChange}
            required
            size="small"
            slotProps={{ input: { sx: { borderRadius: '8px' } } }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth size="small" disabled>
            <InputLabel>User Category</InputLabel>
            <Select value={userCategory} label="User Category" sx={{ borderRadius: '8px' }}>
              <MenuItem value={userCategory}>{userCategory}</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            label="Role Description"
            name="roleDescription"
            value={formData.roleDescription}
            onChange={handleInputChange}
            multiline
            rows={2}
            size="small"
            slotProps={{ input: { sx: { borderRadius: '8px' } } }}
          />
        </Grid>

        <Grid size={12}>
          <Divider sx={{ my: 1 }}>
            <Chip
              label="Permissions Matrix"
              size="small"
              icon={<SecurityOutlined />}
              sx={{ fontWeight: 700 }}
            />
          </Divider>

          <Box
            sx={{
              mt: 2,
              maxHeight: '400px',
              overflowY: 'auto',
              p: 2,
              bgcolor: alpha('#F4F6F8', 0.5),
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {permissions.length > 0 ? (
              <Grid container spacing={3}>
                {permissions.map((Permission) => (
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={Permission.permission}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: '10px',
                        border: '1px solid',
                        borderColor: alpha('#0c5bab', 0.1),
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          mb: 1,
                          color: 'primary.main',
                          fontSize: '0.75rem',
                        }}
                      >
                        {Permission.permission}
                      </Typography>
                      <Stack>
                        {Permission.privilege.map((privilege) => (
                          <FormControlLabel
                            key={privilege.privilegeId}
                            control={
                              <Checkbox
                                id={privilege.privilegeId}
                                size="small"
                                checked={permissionsList.includes(privilege.privilegeId)}
                                onChange={handleCheckboxChange}
                              />
                            }
                            label={
                              <Typography
                                variant="body2"
                                sx={{ textTransform: 'capitalize', fontSize: '0.85rem' }}
                              >
                                {privilege.privilegeName.toLowerCase()}
                              </Typography>
                            }
                          />
                        ))}
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Stack alignItems="center" py={4} spacing={1}>
                <CircularProgress size={24} />
                <Typography variant="caption" color="text.secondary">
                  Loading Permissions...
                </Typography>
              </Stack>
            )}
          </Box>
        </Grid>

        <Grid size={12}>
          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="text"
              onClick={closeEditModal}
              disabled={loading}
              startIcon={<CloseOutlined />}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={16} color="inherit" /> : <SaveOutlined />
              }
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', px: 4 }}
            >
              Save Changes
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EditRoleForm;
